const clpFormatter = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  maximumFractionDigits: 0,
});

export function formatCLP(monto: number): string {
  return clpFormatter.format(monto);
}

/**
 * Formatea un RUT chileno canónico (cuerpo numérico + guión + DV) al
 * formato visual con puntos separadores de miles.
 *
 * Sprint 15 S15-01. Storage = `12345678-5`, display = `12.345.678-5`.
 * Mismo helper que en frontend-portal/lib/format.ts.
 */
export function formatRut(raw: string | null | undefined): string {
  if (!raw) return '';
  const idx = raw.lastIndexOf('-');
  if (idx === -1) return raw;
  const cuerpo = raw.slice(0, idx);
  const dv = raw.slice(idx + 1);
  if (!/^\d+$/.test(cuerpo)) return raw;
  const conPuntos = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${conPuntos}-${dv}`;
}

export function enmascararEmail(email: string | null | undefined): string {
  if (!email) return '—';
  const arroba = email.indexOf('@');
  if (arroba <= 0) return email;
  const nombre = email.slice(0, arroba);
  const dominio = email.slice(arroba + 1);
  const primera = nombre.charAt(0);
  return `${primera}.••••@${dominio}`;
}

export function enmascararCelular(
  celular: string | null | undefined,
): string {
  if (!celular) return '—';
  const digitos = celular.replace(/\D/g, '');
  if (digitos.length < 4) return celular;
  const ultimos4 = digitos.slice(-4);
  // Si tenemos al menos código país + número, formatear como chileno.
  if (digitos.length >= 11 && digitos.startsWith('56')) {
    return `+56 9 •••• ${ultimos4}`;
  }
  if (digitos.length === 9 && digitos.startsWith('9')) {
    return `+56 9 •••• ${ultimos4}`;
  }
  return `•••• ${ultimos4}`;
}

export function enmascararRUT(rut: string | null | undefined): string {
  if (!rut) return '—';
  // RUT chileno: cuerpo + DV. Acepta formato con puntos y guion o sin.
  const limpio = rut.replace(/[.\s]/g, '');
  const partes = limpio.split('-');
  if (partes.length !== 2) return rut;
  const [cuerpo, dv] = partes;
  if (cuerpo.length < 2) return rut;
  const primero = cuerpo.charAt(0);
  return `${primero}.***.***-${dv}`;
}

export function formatFecha(iso: string): string {
  // ISO con tiempo o solo YYYY-MM-DD. Para fechas sin tiempo, parsea local
  // para evitar shift de timezone (UTC midnight = día anterior en Chile).
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const date = dateOnly ? new Date(`${iso}T00:00:00`) : new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

/**
 * Valida un RUT chileno con módulo 11 y devuelve el resultado + el RUT
 * canónico (`XXXXXXXX-Y`, sin puntos, DV K mayúscula). Réplica de la
 * doctrina del backend (`normalizar_rut_chileno`) y del helper homónimo del
 * portal, para validar en el cliente antes del submit. Doctrina RUT H1-H4.
 */
export function validarRutChileno(raw: string | null | undefined): {
  valid: boolean;
  canonico: string | null;
} {
  if (!raw) return { valid: false, canonico: null };
  const limpio = raw.replace(/\./g, '').replace(/\s/g, '').toUpperCase();
  const m = /^(\d{1,8})-([0-9K])$/.exec(limpio);
  if (!m) return { valid: false, canonico: null };
  const cuerpo = parseInt(m[1], 10);
  const dvEntrada = m[2];
  if (cuerpo <= 0 || cuerpo > 99_999_999) {
    return { valid: false, canonico: null };
  }
  const multiplicadores = [2, 3, 4, 5, 6, 7];
  let total = 0;
  const cuerpoStr = String(cuerpo);
  for (let i = 0; i < cuerpoStr.length; i++) {
    const digito = parseInt(cuerpoStr[cuerpoStr.length - 1 - i], 10);
    total += digito * multiplicadores[i % 6];
  }
  const resto = total % 11;
  const dvNum = 11 - resto;
  const dvEsperado = dvNum === 11 ? '0' : dvNum === 10 ? 'K' : String(dvNum);
  if (dvEsperado !== dvEntrada) {
    return { valid: false, canonico: null };
  }
  return { valid: true, canonico: `${cuerpo}-${dvEsperado}` };
}

/**
 * Formatea un RUT chileno mientras el usuario escribe (`XX.XXX.XXX-K`).
 * Réplica del helper del portal. Acepta cualquier mezcla de dígitos,
 * puntos, guión y K. Devuelve formato parcial mientras se escribe.
 */
export function formatearRutEnVivo(raw: string): string {
  const limpio = raw.replace(/[^0-9kK]/g, '').toUpperCase();
  if (limpio.length === 0) return '';
  if (limpio.length === 1) return limpio;
  const cuerpo = limpio.slice(0, -1);
  const dv = limpio.slice(-1);
  const cuerpoConPuntos = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${cuerpoConPuntos}-${dv}`;
}
